import { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, Trash2, CircleAlert } from 'lucide-react';
import type { TBAccount, JournalEntryHeader } from '../types';
import { 
  getAllGroupings, 
  allowsSubGrouping,
  FORM_1120_GROUPINGS,
  FS_REF_LIST
} from '../utils/mappings';
import { displayAmountRounded } from '../utils/format';
import SubGroupingCombobox from './SubGroupingCombobox';

interface BulkActionPanelProps {
  selectedAccountIds: Set<string>;
  accounts: TBAccount[];
  engagementId: string; // Phase 3: needed to load SubGroupings
  onClose: () => void;
  onBulkUpdate: (updates: BulkUpdate) => Promise<void>;
  onBulkDelete: () => Promise<void>;
  mAdjHeaders: JournalEntryHeader[];
}

export interface BulkUpdate {
  grouping?: string;
  subGrouping?: string;
  mAdjRef?: string;
}

export default function BulkActionPanel({
  selectedAccountIds,
  accounts,
  engagementId,
  onClose,
  onBulkUpdate,
  onBulkDelete,
  mAdjHeaders
}: BulkActionPanelProps) {
  // Form state
  const [grouping, setGrouping] = useState<string>('');
  const [subGrouping, setSubGrouping] = useState<string>('');
  const [mAdjRef, setMAdjRef] = useState<string>('');

  // Combobox input state (Grouping & M-Adj — SubGrouping handled by shared component)
  const [groupingInput, setGroupingInput] = useState('');
  const [mAdjRefInput, setMAdjRefInput] = useState('');

  // Dropdown open state
  const [groupingOpen, setGroupingOpen] = useState(false);
  const [mAdjRefOpen, setMAdjRefOpen] = useState(false);


  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  // Refs for click outside detection
  const groupingRef = useRef<HTMLDivElement>(null);
  const mAdjRefRef = useRef<HTMLDivElement>(null);

  // Get selected accounts
  const selectedAccounts = accounts.filter(acc => selectedAccountIds.has(acc.id!));

  // Auto-clear SubGrouping if selected Grouping doesn't allow it
  useEffect(() => {
    if (grouping && !allowsSubGrouping(grouping)) {
      setSubGrouping('');
    }
  }, [grouping]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (groupingRef.current && !groupingRef.current.contains(event.target as Node)) {
        setGroupingOpen(false);
      }
      if (mAdjRefRef.current && !mAdjRefRef.current.contains(event.target as Node)) {
        setMAdjRefOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on input
  const filteredGroupings = getAllGroupings().filter(g => 
    g.toLowerCase().includes(groupingInput.toLowerCase())
  );

  const filteredMAdjHeaders = mAdjHeaders.filter(h =>
    h.jeNumber.toLowerCase().includes(mAdjRefInput.toLowerCase()) ||
    h.jeName.toLowerCase().includes(mAdjRefInput.toLowerCase())
  );

  const handleGroupingSelect = (value: string) => {
    setGrouping(value);
    setGroupingInput(value);
    setGroupingOpen(false);
  };

  const handleMAdjRefSelect = (value: string) => {
    setMAdjRef(value);
    setMAdjRefInput(value);
    setMAdjRefOpen(false);
  };

  const handleSave = async () => {
    // Validate that at least one field is selected
    if (!grouping && !subGrouping && !mAdjRef) {
      setValidationError('Please select at least one field to update');
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 500);
      return;
    }

    setValidationError('');
    setIsSubmitting(true);

    try {
      const updates: BulkUpdate = {};
      
      if (grouping) updates.grouping = grouping;
      if (subGrouping) updates.subGrouping = subGrouping;
      if (mAdjRef) updates.mAdjRef = mAdjRef;

      await onBulkUpdate(updates);
      
      // Clear form after successful save
      setGrouping('');
      setSubGrouping('');
      setMAdjRef('');
      setGroupingInput('');
      setMAdjRefInput('');
    } catch (error) {
      console.error('Bulk update error:', error);
      setValidationError(error instanceof Error ? error.message : 'Failed to update accounts');
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onBulkDelete();
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Bulk delete error:', error);
      setValidationError(error instanceof Error ? error.message : 'Failed to delete accounts');
      setShouldShake(true);
      setTimeout(() => setShouldShake(false), 500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      {/* Slide-out Panel */}
      <div className="fixed right-0 top-0 h-full w-96 glass-panel border-l-2 border-slate-200 dark:border-slate-800 shadow-2xl z-40 flex flex-col transition-colors duration-300">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b-2 border-slate-200 dark:border-slate-800">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Bulk Action Panel
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            title="Close and clear selection"
          >
            <X className="w-5 h-5 text-taxtrail-red dark:text-red-400" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className={`flex-1 overflow-y-auto p-4 space-y-4 ${shouldShake ? 'animate-shake' : ''}`}>
          {/* Validation Error */}
          {validationError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
              <CircleAlert className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-400">{validationError}</p>
            </div>
          )}

          {/* Grouping Combobox */}
          <div ref={groupingRef} className="relative">
            <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
              Grouping
            </label>
            <div className="relative">
              <input
                type="text"
                value={groupingInput}
                onChange={(e) => {
                  setGroupingInput(e.target.value);
                  setGroupingOpen(true);
                }}
                onFocus={() => setGroupingOpen(true)}
                placeholder="Select or search…"
                className="w-full pl-3 pr-10 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-slate-200 dark:border-slate-700 transition-colors duration-200"
              />
              <button
                onClick={() => setGroupingOpen(!groupingOpen)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${groupingOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {/* Dropdown — Tiered with FS Ref headers */}
            {groupingOpen && (
              <div className="absolute z-50 w-full mt-1 glass-panel rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div
                  onClick={() => handleGroupingSelect('')}
                  className="px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700/60 cursor-pointer text-sm text-slate-500 dark:text-slate-400 italic transition-colors"
                >
                  No change
                </div>
                {FS_REF_LIST.map((fsRef) => {
                  const groupingsForRef = (FORM_1120_GROUPINGS[fsRef] || []).filter(g =>
                    g.toLowerCase().includes(groupingInput.toLowerCase())
                  );
                  if (groupingsForRef.length === 0) return null;
                  return (
                    <div key={fsRef}>
                      <div className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-charcoal-700 dark:text-charcoal-200 bg-slate-100 dark:bg-slate-800 select-none border-t border-slate-200 dark:border-slate-700">
                        {fsRef}
                      </div>
                      {groupingsForRef.map((g) => (
                        <div
                          key={g}
                          onClick={() => handleGroupingSelect(g)}
                          className={`pl-6 pr-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700/60 cursor-pointer text-sm text-slate-900 dark:text-slate-100 transition-colors ${
                            grouping === g ? 'bg-sage-50 dark:bg-sage-900/20' : ''
                          }`}
                        >
                          {g}
                        </div>
                      ))}
                    </div>
                  );
                })}
                {filteredGroupings.length === 0 && (
                  <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400 italic">
                    No results found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SubGrouping — Shared Combobox (only when grouping allows it) */}
          {(!grouping || allowsSubGrouping(grouping)) && (
            <div>
              <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
                SubGrouping
              </label>
              <SubGroupingCombobox
                engagementId={engagementId}
                value={subGrouping}
                onChange={setSubGrouping}
                disabled={isSubmitting}
                showNoneOption={true}
                noneLabel="No change"
              />
            </div>
          )}

          {/* M-Adjustment Reference Combobox */}
          <div ref={mAdjRefRef} className="relative">
            <label className="block text-sm font-medium mb-1 text-slate-900 dark:text-slate-100">
              M-Adjustment Reference
            </label>
            <div className="relative">
              <input
                type="text"
                value={mAdjRefInput}
                onChange={(e) => {
                  setMAdjRefInput(e.target.value);
                  setMAdjRefOpen(true);
                }}
                onFocus={() => setMAdjRefOpen(true)}
                placeholder="Select or search…"
                className="w-full pl-3 pr-10 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-slate-200 dark:border-slate-700 transition-colors duration-200"
              />
              <button
                onClick={() => setMAdjRefOpen(!mAdjRefOpen)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${mAdjRefOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            {/* Dropdown */}
            {mAdjRefOpen && (
              <div className="absolute z-50 w-full mt-1 glass-panel rounded-lg shadow-lg max-h-60 overflow-y-auto">
                <div
                  onClick={() => handleMAdjRefSelect('')}
                  className="px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700/60 cursor-pointer text-sm text-slate-500 dark:text-slate-400 italic transition-colors"
                >
                  No change
                </div>
                {filteredMAdjHeaders.map((header) => (
                  <div
                    key={header.id}
                    onClick={() => handleMAdjRefSelect(header.jeNumber)}
                    className={`px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-700/60 cursor-pointer text-sm text-slate-900 dark:text-slate-100 transition-colors ${
                      mAdjRef === header.jeNumber ? 'bg-sage-50 dark:bg-sage-900/20' : ''
                    }`}
                  >
                    {header.jeNumber}: {header.jeName}
                  </div>
                ))}
                {filteredMAdjHeaders.length === 0 && (
                  <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400 italic">
                    No results found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Selected Accounts List - Tabular Format */}
          <div className="mt-6">
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              {/* Sticky Header */}
              <div className="sticky top-0 bg-slate-100 dark:bg-slate-700 px-3 py-2 border-b border-slate-200 dark:border-slate-700 z-10">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  Selected Accounts ({selectedAccounts.length})
                </p>
              </div>

              {/* Scrollable List - Max 10 visible at a time */}
              <div className="max-h-80 overflow-y-auto">
                {selectedAccounts.map((account) => {
                  const { text: balanceText, isNegative } = displayAmountRounded(account.bookBalance);
                  
                  return (
                    <div
                      key={account.id}
                      className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 last:border-b-0 hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {/* Account Number - Fixed Width */}
                        <div className="w-20 flex-shrink-0">
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {account.accountNumber}
                          </p>
                        </div>
                        
                        {/* Account Name - Flex Grow */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                            {account.accountName}
                          </p>
                        </div>
                        
                        {/* Book Balance - Right Aligned */}
                        <div className="w-24 flex-shrink-0 text-right">
                          <p className={`text-sm font-mono ${
                            isNegative 
                              ? 'text-red-600 dark:text-red-400' 
                              : 'text-slate-900 dark:text-slate-100'
                          }`}>
                            {balanceText}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Action Buttons */}
        <div className="flex-shrink-0 p-4 border-t-2 border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3">
          <button
            onClick={handleDeleteClick}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-taxtrail-red dark:text-red-400 border-2 border-taxtrail-red dark:border-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Delete Selected
          </button>
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="px-6 py-2 text-sm font-medium rounded-lg bg-sage-600 hover:bg-sage-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-sage-500"
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm">
          <div className="glass-panel rounded-xl shadow-2xl max-w-md w-full mx-4 transition-colors duration-300">
            <div className="p-6">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                  <CircleAlert className="w-6 h-6 text-taxtrail-red dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                    Delete {selectedAccountIds.size} Account{selectedAccountIds.size !== 1 ? 's' : ''}?
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    This action cannot be undone. All selected accounts will be permanently deleted.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3 justify-end">
              <button
                onClick={handleDeleteCancel}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium bg-taxtrail-red dark:bg-red-600 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shake Animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        .animate-shake {
          animation: shake 0.5s;
        }
      `}</style>
    </>
  );
}
